/**
 * What an assistant connected to Dantria can do, written once.
 *
 * This copy has four audiences and used to have four separate answers, which
 * is how a product ends up advertising a feature it removed two releases ago:
 *
 *   - the operator deciding whether to hand out a credential (Settings →
 *     Integrations);
 *   - the operator who has just connected one and is looking at an empty chat
 *     (the post-connection page, and the hints beside the work itself);
 *   - the **assistant**, when somebody types "what can you actually do with my
 *     Dantria?" — which without this is answered by improvising from a list of
 *     tool names, and improvising produces a confident description of features
 *     that do not exist;
 *   - and a person attaching the guide as a resource to read for themselves.
 *
 * So it lives in `core` — the one package both the Worker and the browser can
 * import — as data rather than prose baked into a component, and every entry
 * names the `tools` that actually deliver it. That last field is the only thing
 * keeping this honest: a test asserts every name here exists in the server's
 * real tool list, so deleting a tool breaks the build rather than quietly
 * leaving a promise behind in three places.
 *
 * Written for somebody who runs a contracting business, not for somebody who
 * knows what MCP is. "It can price work from measurements" is the sentence
 * they are deciding about; `price_work` is not.
 */

export interface McpCapability {
  /** The capability, as the person deciding would name it. */
  what: string;
  /** One line on what it means in practice. */
  detail: string;
  /** The tools behind it. Checked against the live tool list by a test. */
  tools: readonly string[];
}

export interface McpExample {
  /** A short label for grouping: "Quoting", "Revising". */
  about: string;
  /** A sentence a person can say or paste verbatim. */
  text: string;
}

export const MCP_CAPABILITIES: readonly McpCapability[] = [
  {
    what: "Read your price book",
    detail: "Product types, work stages, and both sell and cost rates.",
    tools: ["list_product_types", "list_price_items"],
  },
  {
    what: "Price work from measurements",
    detail:
      "“89 inches of cabinet, three rows” priced against your own rates, minimum charges " +
      "included. Nothing is saved.",
    tools: ["price_work"],
  },
  {
    what: "Add clients",
    detail: "Matched on email first, so the same client is never created twice.",
    tools: ["list_clients", "create_client"],
  },
  {
    what: "Build proposals",
    detail: "Segments, line items and draw schedules, from your proposal template.",
    tools: ["create_proposal"],
  },
  {
    what: "Revise one afterwards",
    detail: "Change a price, add a part of the job, rewrite a draw schedule — not a rebuild.",
    tools: ["update_proposal_segments"],
  },
  {
    what: "Write proposal prose",
    detail: "Scope of work and the like, as markdown. Money tables stay live, never prose.",
    tools: ["set_proposal_prose"],
  },
  {
    what: "Track where things stand",
    detail: "What is still a draft, what is out with a client, what has been accepted.",
    tools: ["list_proposals", "get_proposal", "set_proposal_status"],
  },
  {
    what: "Convert a proposal to a contract",
    detail: "Once the client has accepted it. The contract gets its own number and wording.",
    tools: ["convert_proposal_to_contract"],
  },
  {
    what: "Approve a contract into projects",
    detail:
      "Only if you ticked that box on the key. Every segment becomes a project with its " +
      "billing milestones, and it posts to your books.",
    tools: ["approve_contract"],
  },
  {
    what: "Share a document",
    detail: "A link that opens the proposal and downloads the PDF or Word file, with no login.",
    tools: ["create_document_link"],
  },
];

/**
 * What it cannot do, said as plainly as what it can.
 *
 * A person handing out a credential is weighing risk, and the reassuring half
 * of the answer is the half a capability list leaves out. It is also the
 * shortest route to trusting the rest of it.
 */
export const MCP_LIMITS: readonly string[] = [
  "It can never delete anything — not a client, not a proposal, not a record.",
  "It can only approve a contract into live projects if you ticked that box on the key.",
  "It cannot touch invoices, bills, expenses, commissions, your team or your trash.",
  "It acts on one company — the one the key was issued for — and can reach no other.",
];

/**
 * Sentences a person can say, or copy and paste.
 *
 * Deliberately specific, with real measurements and real money in them. A
 * generic example ("ask it to create a proposal") teaches nothing; the whole
 * difficulty for a new user is not knowing how much detail is allowed in one
 * breath, and the answer is: all of it.
 */
export const MCP_EXAMPLES: readonly McpExample[] = [
  {
    about: "Pricing",
    text: "Price 85 inches of stone with a 20 inch backsplash and a 2 inch mitre off my prices.",
  },
  {
    about: "Quoting",
    text:
      "Quote the Harper Residence, hello@harper.com — a kitchen with 3 rows of cabinets at " +
      "89 inches, stone at 85 inches with a 20 inch backsplash. Use my prices. Bill it 30/40/30.",
  },
  {
    about: "Revising",
    text: "On PRO-0004, make the stone $34,000 and add a walk-in closet at $5,000.",
  },
  {
    about: "Wording",
    text: "Rewrite the scope of work on PRO-0004 to mention we're handling demolition.",
  },
  {
    about: "Chasing",
    text: "Which proposals have I sent that nobody has come back on?",
  },
  {
    about: "Sending",
    text: "Give me a link to PRO-0004 that the client can open.",
  },
  {
    about: "Closing",
    text: "The Harpers signed PRO-0004. Convert it and walk me through approving the contract.",
  },
];

/** The four starters the server advertises as MCP prompts, by name and title.
 *  Kept here so the app can list them without the web bundle importing
 *  anything from the Worker. A test asserts they match the real prompts. */
export const MCP_STARTERS: readonly { name: string; title: string; detail: string }[] = [
  {
    name: "quote_a_job",
    title: "Quote a job",
    detail: "Measurements to a proposal with a shareable link.",
  },
  {
    name: "price_this",
    title: "Price this off my book",
    detail: "What would this cost? Saves nothing.",
  },
  {
    name: "proposal_status",
    title: "Where do my proposals stand",
    detail: "What's out, what's gone quiet, what's waiting on you.",
  },
  {
    name: "sign_off_contract",
    title: "Turn a signed contract into projects",
    detail: "Convert, then approve, one step at a time.",
  },
];

/**
 * The same thing as a document, for an assistant to read or a person to attach.
 *
 * Markdown because both of those readers want prose: a model that is asked
 * "what can you do?" should answer out of this rather than out of a tool list,
 * and a person attaching it wants something that reads like a page.
 */
export function mcpGuideMarkdown(companyName: string): string {
  const lines: string[] = [
    `# What I can do with ${companyName}'s Dantria`,
    "",
    "Dantria is this company's project cost management system. I'm connected to it, so I " +
      "can price work against their own rates and build the documents that come out of that.",
    "",
    "## What I can do",
    "",
  ];

  for (const capability of MCP_CAPABILITIES) {
    lines.push(`- **${capability.what}.** ${capability.detail}`);
  }

  lines.push("", "## What I can't do", "");
  for (const limit of MCP_LIMITS) lines.push(`- ${limit}`);

  lines.push(
    "",
    "## How to ask",
    "",
    "Say the whole thing in one go — measurements, client, how it bills. There's no need " +
      "to break it into steps.",
    "",
  );

  for (const example of MCP_EXAMPLES) {
    lines.push(`- *${example.about}:* “${example.text}”`);
  }

  lines.push(
    "",
    "## Two rules I follow",
    "",
    "1. **I read the price book before quoting.** Measurements go in the unit this company " +
      "actually prices in — I don't guess that cabinets go by the foot.",
    "2. **Every amount is exact.** Money here is decimal arithmetic to the cent, never a " +
      "floating-point number.",
    "",
    "If you want me to start somewhere, the prompts on this connection — Quote a job, " +
      "Price this off my book, Where do my proposals stand, Turn a signed contract into " +
      "projects — walk each job in the right order.",
  );

  return lines.join("\n");
}
